import type {
  CompanyDetail,
  PayloadCreateTeam,
  RequestBodyCompany,
  RequestQueryCompany,
  RequestQueryCompanyOverview,
  RequestQueryGenerateImportUserTemplate,
  ResponseCompanyOverview,
  ResponseGenerateImportUserTemplate,
  ResponseOnBoardChecklist,
  TeamDetail,
  TeamLevelCategory,
} from "@/utils/types/company";
import type {
  PaginationResponse,
  PaginationResponseWithMeta,
} from "@/utils/types/common";

export const useCompanyStore = defineStore("company-store", () => {
  const getCompanies = async (query?: RequestQueryCompany) => {
    let url = `/company/v1/companies/`;
    if (query) {
      const params = new URLSearchParams(convertToStringObject(query));
      url += "?" + params;
    }
    const res = await useApi<PaginationResponse<CompanyDetail>>(url, {
      method: "get",
    });
    return res;
  };

  const createCompany = async (body: RequestBodyCompany) => {
    const res = await useApi<CompanyDetail>(`/company/v1/companies/`, {
      method: "POST",
      body: body,
    });
    return res;
  };

  const getCompanyById = async (id: number | string) => {
    const res = await useApi<CompanyDetail>(`/company/v1/companies/${id}`, {
      method: "GET",
    });
    return res;
  };

  const updateCompanyById = async (
    id: number | string,
    body: RequestBodyCompany,
  ) => {
    const res = await useApi<CompanyDetail>(`/company/v1/companies/${id}`, {
      method: "patch",
      body: body,
    });
    return res;
  };

  // const checkCompanyCanDeleteById = async (id: number | string) => {
  //   const res = await useApi<{
  //     success: boolean;
  //     can_delete: boolean;
  //     user_count: number;
  //     company_id: number;
  //   }>(`/api/admin/companies/can_delete/?company_id=${id}`, {
  //     method: "get",
  //   });
  //   return res;
  // };

  const deleteCompanyById = async (id: number | string) => {
    const res = await useApi(`/company/v1/companies/${id}`, {
      method: "DELETE",
    });
    return res;
  };

  // const mergeCompany = async (body: any) => {
  //   const res = await useApi<CommonResponseWithData<CompanyDetail>>(
  //     `/api/admin/companies/merge/`,
  //     {
  //       method: "POST",
  //       body: body,
  //     }
  //   );
  //   return res;
  // };

  const toggleCompanyStatusById = async (id: number | string) => {
    const res = await useApi<CompanyDetail>(
      `/company/v1/companies/${id}/toggle/active`,
      {
        method: "POST",
      },
    );
    return res;
  };

  const getOnboardChecklist = async () => {
    const res = await useApi<ResponseOnBoardChecklist>(
      `/company/v1/companies/onboard-checklist`,
      {
        method: "GET",
      },
    );
    return res;
  };

  const getCompanyOverview = async (query?: RequestQueryCompanyOverview) => {
    let url = `/company/v1/companies/overview`;
    if (query) {
      const params = new URLSearchParams(convertToStringObject(query));
      url += "?" + params;
    }

    const res = await useApi<
      PaginationResponseWithMeta<ResponseCompanyOverview>
    >(url, {
      method: "GET",
    });
    return res;
  };

  const getTeamsByCompanyId = async (
    companyId: number | string,
    query?: any,
  ) => {
    let url = `/company/v1/companies/${companyId}/teams`;
    if (query) {
      const params = new URLSearchParams(convertToStringObject(query));
      url += "?" + params;
    }
    const res = await useApi<PaginationResponse<TeamDetail>>(url, {
      method: "GET",
    });
    return res;
  };

  const createTeam = async (
    companyId: number | string,
    body: PayloadCreateTeam,
  ) => {
    const res = await useApi<TeamDetail>(
      `/company/v1/companies/${companyId}/teams`,
      {
        method: "POST",
        body,
      },
    );
    return res;
  };

  const generateUserImportTemplate = async (
    query?: RequestQueryGenerateImportUserTemplate,
  ) => {
    let url = `/company/v1/companies/generate-user-import-template`;
    if (query) {
      const params = new URLSearchParams(convertToStringObject(query));
      url += "?" + params;
    }
    const res = await useApi<ResponseGenerateImportUserTemplate>(url, {
      method: "GET",
    });
    return res;
  };

  const getTeamLevelCategory = async (teamId: number | string) => {
    const url = `/company/v1/teams/${teamId}/level-categories`;
    const res = await useApi<TeamLevelCategory>(url, {
      method: "GET",
    });
    return res;
  };

  return {
    getCompanies,
    createCompany,
    getCompanyById,
    deleteCompanyById,
    updateCompanyById,
    toggleCompanyStatusById,
    getOnboardChecklist,
    getCompanyOverview,
    getTeamsByCompanyId,
    createTeam,
    getTeamLevelCategory,

    generateUserImportTemplate,
  };
});
